/**
 * Configuration domains, as the backend names them.
 *
 * A requirement group's children each carry one, a change belongs to exactly
 * one, and the group screen uses them as section headings — so they are
 * declared once here in the order the backend enumerates them
 * (`RequirementGroup.Domain`) rather than re-listed per screen.
 *
 * `CHANGE_DOMAINS` is the wider set: five of them carry no revisioned payload
 * slice and so have no Columbia section to render, but a change can still
 * belong to one and needs a label.
 */

/**
 * The one domain whose name is not a valid camelCase identifier. Referenced
 * through this constant so the maps below can be keyed by the backend's own
 * spelling — the shared vocabulary is worth more than a rename — without an
 * eslint suppression on every map. A computed key from a literal `const` keeps
 * the key's type, so exhaustiveness still holds.
 */
export const LIFE_EVENTS = 'life_events';

export const CONFIGURATION_DOMAINS = [
  'details',
  'content',
  'selections',
  'eligibility',
  'enrolment',
  'pricing',
  'windows',
  LIFE_EVENTS,
] as const;
export type ConfigurationDomain = (typeof CONFIGURATION_DOMAINS)[number];

/** Change domains with no revisioned payload slice, so no section to render. */
type UnrevisionedDomain =
  | 'creation'
  | 'approvals'
  | 'features'
  | 'operations'
  | 'renewals';
export type ChangeDomain = ConfigurationDomain | UnrevisionedDomain;

const DOMAIN_LABEL = {
  details: 'Details',
  content: 'Content',
  selections: 'Selections',
  eligibility: 'Eligibility',
  enrolment: 'Enrolment',
  pricing: 'Pricing',
  windows: 'Windows',
  [LIFE_EVENTS]: 'Life events',
  creation: 'Creation',
  approvals: 'Approvals',
  features: 'Features',
  operations: 'Operations',
  renewals: 'Renewals',
} satisfies Record<ChangeDomain, string>;

/** Section order on a group screen. */
export const DOMAIN_ORDER: readonly string[] = CONFIGURATION_DOMAINS;

const isDomain = (value: string): value is ChangeDomain =>
  value in DOMAIN_LABEL;

/**
 * A domain read off an API response or a URL. Falls back to the raw value
 * rather than hiding it, so a domain added on the backend before it is added
 * here still reads as itself.
 */
export const domainLabel = (domain: string | null | undefined) => {
  if (!domain) return 'No domain yet';
  return isDomain(domain) ? DOMAIN_LABEL[domain] : domain;
};
